import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Download, ClipboardList, Trophy, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import api from "@/lib/api";
import { valueLabel, tallyLabel } from "@/i18n";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/pages/Applications";
import AiComparison from "@/components/AiComparison";

const yesCount = (r) => r.scores?.invited_to_interview?.yes || 0;
const maybeCount = (r) => r.scores?.invited_to_interview?.maybe || 0;
// interview support, for sorting the interview column
const interviewSupport = (r) => yesCount(r) + maybeCount(r) * 0.5;

// first click on these columns sorts ascending, on all others descending
const ASCENDING_FIRST = ["rank", "applicant", "university", "status"];

const SortIcon = ({ active, dir }) =>
  active ? (dir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
         : <ChevronsUpDown className="w-3 h-3 opacity-40" />;

export default function Results() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sort, setSort] = useState({ key: "rank", dir: "asc" });
  const [tab, setTab] = useState("evaluators");
  const [ai, setAi] = useState(null);

  useEffect(() => {
    api.get("/ai-reviews").then((r) => setAi(r.data)).catch(() => setAi(null));
  }, []);

  useEffect(() => {
    api.get("/results").then((r) => setData(r.data)).catch(() => setData({ criteria: [], results: [] }));
  }, []);

  const { criteria, results } = data || { criteria: [], results: [] };

  // Rank: average /10 descending, then interview "yes" votes, then "maybe" votes, then name.
  const rankById = useMemo(() => {
    const ranked = [...results].sort((a, b) =>
      b.overall - a.overall ||
      yesCount(b) - yesCount(a) ||
      maybeCount(b) - maybeCount(a) ||
      `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`));
    return Object.fromEntries(ranked.map((r, i) => [r.id, i + 1]));
  }, [results]);

  const displayed = useMemo(() => {
    const value = (r, key) => {
      switch (key) {
        case "rank": return rankById[r.id];
        case "applicant": return `${r.name} ${r.surname}`.toLowerCase();
        case "university": return String(valueLabel(r.university) || "").toLowerCase();
        case "overall": return r.overall;
        case "reviews": return r.reviews_count;
        case "status": return t(`status.${r.status}`, { defaultValue: r.status || "" }).toLowerCase();
        case "invited_to_interview": return interviewSupport(r);
        default: return r.scores?.[key] ?? 0;
      }
    };
    return [...results].sort((a, b) => {
      const va = value(a, sort.key), vb = value(b, sort.key);
      let cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb));
      if (cmp === 0) cmp = rankById[a.id] - rankById[b.id];
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [results, sort, rankById, t]);

  if (!data) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const toggleSort = (key) => setSort((s) => (s.key === key
    ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
    : { key, dir: ASCENDING_FIRST.includes(key) ? "asc" : "desc" }));

  const cellValue = (c, r) => (c.type === "choice" ? tallyLabel(r.scores[c.key]) : (r.scores[c.key] ?? ""));

  const Th = ({ sortKey, label, center, title }) => (
    <th className={`py-4 px-3 font-medium ${center ? "text-center" : "text-left"}`} title={title}>
      <button onClick={() => toggleSort(sortKey)} data-testid={`sort-${sortKey}`}
        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${sort.key === sortKey ? "text-foreground" : ""}`}>
        {label} <SortIcon active={sort.key === sortKey} dir={sort.dir} />
      </button>
    </th>
  );

  const exportCsv = () => {
    const col = (k) => t(`admin.results.csv_${k}`);
    const header = [col("rank"), col("applicant"), col("email"), col("university"), col("field"),
      ...criteria.map((c) => c.label), col("average"), col("reviews"), col("status")];
    const rows = displayed.map((r) => [
      rankById[r.id],
      `${r.name} ${r.surname}`.trim(),
      r.email,
      valueLabel(r.university),
      valueLabel(r.field_of_study),
      ...criteria.map((c) => cellValue(c, r)),
      r.overall,
      `${r.reviews_count}/${r.assigned_count}`,
      t(`status.${r.status}`, { defaultValue: r.status }),
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `penda-evaluation-results-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="animate-fade-in" data-testid="results-page">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-sm text-muted-foreground uppercase tracking-[0.2em] mb-1">{t("admin.results.eyebrow")}</p>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">
            {t("admin.results.title")}
            <span className="text-muted-foreground font-normal text-lg ml-3" data-testid="results-total">
              {t("admin.results.reviewed", { count: results.length })}
            </span>
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{t("admin.results.subtitle")}</p>
        </div>
        <Button onClick={exportCsv} disabled={results.length === 0} variant="outline" className="h-11" data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-2" /> {t("admin.results.download_csv")}
        </Button>
      </div>

      {ai?.applications?.some((a) => a.ai) && (
        <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit mb-6">
          {["evaluators", "ai"].map((k) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`results-tab-${k}`}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === k ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {t(`admin.results.tab_${k}`)}
            </button>
          ))}
        </div>
      )}

      {tab === "ai" ? <AiComparison data={ai} /> : (
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {results.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground text-sm">
            <ClipboardList className="w-8 h-8 mx-auto mb-3 opacity-40" />
            {t("admin.results.empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wider text-left">
                  <Th sortKey="rank" label={t("admin.results.col_rank")} />
                  <Th sortKey="applicant" label={t("admin.results.col_applicant")} />
                  <Th sortKey="university" label={t("admin.results.col_university")} />
                  {criteria.map((c) => (
                    <Th key={c.key} sortKey={c.key} center title={c.label}
                      label={t(`criteria_short.${c.key}`, { defaultValue: c.label })} />
                  ))}
                  <Th sortKey="overall" center label={t("admin.results.col_avg")} />
                  <Th sortKey="reviews" center label={t("admin.results.col_reviews")} />
                  <Th sortKey="status" label={t("admin.results.col_status")} />
                </tr>
              </thead>
              <tbody>
                {displayed.map((r) => {
                  const rank = rankById[r.id];
                  return (
                  <tr key={r.id}
                    onClick={() => navigate(`/admin/applications/${r.id}`)}
                    className="border-t border-border hover:bg-muted/30 transition-colors cursor-pointer"
                    data-testid={`result-row-${r.id}`}>
                    <td className="py-4 px-3">
                      <span className={`inline-flex items-center gap-1 font-semibold ${rank === 1 ? "text-primary" : "text-muted-foreground"}`} data-testid={`result-rank-${r.id}`}>
                        {rank === 1 && <Trophy className="w-3.5 h-3.5" />}{rank}
                      </span>
                    </td>
                    <td className="py-4 px-3">
                      <p className="font-medium text-foreground">{r.name} {r.surname}</p>
                      <p className="text-xs text-muted-foreground">{r.email}</p>
                    </td>
                    <td className="py-4 px-3 text-muted-foreground max-w-[180px] truncate">{valueLabel(r.university) || "—"}</td>
                    {criteria.map((c) => (
                      <td key={c.key} className="py-4 px-3 text-center text-muted-foreground whitespace-nowrap" data-testid={`result-${r.id}-${c.key}`}>
                        {cellValue(c, r)}
                      </td>
                    ))}
                    <td className="py-4 px-3 text-center">
                      <span className="font-heading text-lg font-semibold text-primary" data-testid={`result-avg-${r.id}`}>{r.overall}</span>
                      <span className="text-xs text-muted-foreground">/10</span>
                    </td>
                    <td className="py-4 px-3 text-center text-muted-foreground">{r.reviews_count}/{r.assigned_count}</td>
                    <td className="py-4 px-3"><StatusBadge status={r.status} /></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
